/**
 * AuthProvider — wraps the existing Keycloak BFF auth into React context.
 *
 * Reads the apex_user_info cookie for identity, calls /auth/me to load
 * the full user record, and redirects to Keycloak login when unauthenticated.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { formatRepName } from '../lib/format-name';
import { configurePickerContextUrl } from '../hooks/useMyNumbers';
import { loadPermissions } from '../../lib/permissions';

// ── Types ────────────────────────────────────────────────────────────────

export interface UserOrganization {
  id: string;
  name: string;
}

export interface User {
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role: string;
  sub?: string;
  platformOps?: boolean;
  agencyOwner?: boolean;
  /**
   * True when the user's only role is agency_owner (no hierarchy role).
   * The shell collapses to the AO portal: portal-only nav, portal default
   * route, no rep cockpit (softphone, clock, inbox). Set by the server in
   * the apex_user_info cookie — the client can't derive it from `role`,
   * which reads 'rep' for a pure agency owner.
   */
  portalOnly?: boolean;
  organizations?: UserOrganization[];
  activeOrgId?: string;
  activeOrgName?: string;
}

/**
 * Why tenant resolution failed for the current user. Matches the server's
 * /auth/tenant-status response shape. Consumers render a dedicated error
 * page when this is non-null — every other route would just 403-storm.
 */
/** The user a platform-ops operator is currently viewing as (from /auth/me). */
export interface ImpersonationTarget {
  id: string;
  email: string;
  name: string;
  role: string;
}

export interface TenantResolutionError {
  reason: 'no_org_claim' | 'no_tenant_for_org' | 'no_membership';
  diagnostics: {
    sub: string;
    email: string;
    organizationId: string | null;
    timestamp: string;
  };
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Set when the user authenticated but the server couldn't resolve them
   * to a tenant (missing org claim, unmapped org, or no membership row).
   * When this is non-null the shell renders the tenant-error page — it's
   * the explicit "don't loop back to login" signal.
   */
  tenantError: TenantResolutionError | null;
  /**
   * The user's real role — what they actually are before any impersonation.
   * For platform-ops users this is always 'platform-ops'. Off-palette rendering
   * (#616) uses this to decide which features to keep visible when impersonating.
   */
  realRole: string;
  /**
   * The role the user is currently acting as. Equals realRole unless a
   * platform-ops user has selected "view as" via the UserSwitcher, in which
   * case it's the impersonated user's membership role (server-resolved).
   */
  effectiveRole: string;
  /**
   * True when a platform-ops user has an active "view as" selection.
   * Regular users can never impersonate, so this is always false for them.
   */
  isImpersonating: boolean;
  /**
   * The user being impersonated, or null. Server-authoritative — comes from
   * the /auth/me `impersonation` block, not from parsing the cookie (the
   * `apex_assume_user` cookie only carries the target's id).
   */
  impersonationTarget: ImpersonationTarget | null;
  /**
   * True when the shell should render the AO portal-only experience:
   * either the user really is portal-only (server-computed flag), or a
   * platform-ops user is impersonating agency_owner. The shell, router,
   * and sidebar all read this instead of `user.portalOnly` so that
   * impersonation reproduces exactly what a portal-only owner sees.
   */
  portalView: boolean;
  login: (returnTo?: string) => void;
  /**
   * `forgetLoginMethod` drops the remembered sign-in door so the next visit
   * lands on the picker instead of jumping back to the same one.
   */
  logout: (opts?: { forgetLoginMethod?: boolean }) => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  /**
   * Rotate the JWT and reload the user record from the server. Called when
   * the server pushes `auth.refresh_required` over WebSocket because an
   * admin changed this user's role or permissions — the old JWT still
   * carries the stale claim, so we exchange it before re-reading /auth/me.
   * No-op on refresh failure; the user's state stays intact.
   */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Cookie helpers ───────────────────────────────────────────────────────

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
  return match ? (match[1] ?? null) : null;
}

function parseUserInfo(): User | null {
  const raw = getCookie('apex_user_info');
  if (!raw) return null;
  try {
    let b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

// ── apiFetch with 401 auto-refresh ──────────────────────────────────────
// Lives in `../lib/auth-fetch` so non-React modules (idle tracker, etc.)
// can import without pulling .tsx into a JSX-disabled tsconfig. Re-exported
// here so existing call sites importing from this provider don't change.
import { refreshToken, apiFetch } from '../lib/auth-fetch';
import { redirectToLogin } from '../lib/login-redirect';
export { refreshToken, apiFetch };

// ── Provider ─────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tenantError, setTenantError] = useState<TenantResolutionError | null>(null);
  /**
   * Server-resolved impersonation state from /auth/me. The UserSwitcher
   * sets/clears the `apex_assume_user` cookie and reloads the page, so
   * this only changes across full loads.
   */
  const [impersonation, setImpersonation] = useState<{
    realRole: string;
    target: ImpersonationTarget;
  } | null>(null);

  /**
   * Check whether the current user resolves to a tenant on the server.
   * Runs alongside every /auth/me load so the shell can short-circuit to
   * the error page instead of 403-storming downstream API calls.
   */
  const checkTenantStatus = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch('/auth/tenant-status', { credentials: 'same-origin' });
      if (!res.ok) {
        // Unexpected — don't trap the user on the error page, let downstream
        // calls surface whatever's actually wrong.
        setTenantError(null);
        return;
      }
      const data = (await res.json()) as
        | { ok: true; scope: 'tenant' | 'platform_ops' | 'service_account' }
        | (TenantResolutionError & { ok: false });
      if (data.ok) {
        setTenantError(null);
      } else {
        setTenantError({ reason: data.reason, diagnostics: data.diagnostics });
      }
    } catch {
      setTenantError(null);
    }
  }, []);

  /**
   * Read the cookie, call /auth/me, and commit the result to state. Shared
   * by the initial mount and the post-`auth.refresh_required` reload.
   */
  const loadUserFromServer = useCallback(async (): Promise<void> => {
    const cookieUser = parseUserInfo();
    if (!cookieUser?.email) return;
    const orgFields = {
      organizations: cookieUser.organizations,
      activeOrgId: cookieUser.activeOrgId,
      activeOrgName: cookieUser.activeOrgName,
    };
    try {
      const res = await apiFetch('/auth/me');
      if (res.ok) {
        const data = await res.json();
        setImpersonation(data.impersonation ?? null);
        if (data.rep) {
          setUser({
            ...data.rep,
            platformOps: cookieUser.platformOps,
            agencyOwner: cookieUser.agencyOwner,
            portalOnly: cookieUser.portalOnly,
            ...orgFields,
          });
        } else if (data.user) {
          setUser({
            email: data.user.email,
            name: data.user.name || formatRepName(data.user),
            firstName: data.user.firstName,
            lastName: data.user.lastName,
            role: data.user.role || 'rep',
            sub: data.user.id,
            platformOps: cookieUser.platformOps,
            agencyOwner: cookieUser.agencyOwner,
            portalOnly: cookieUser.portalOnly,
            ...orgFields,
          });
        } else {
          setUser(cookieUser);
        }
      } else {
        setUser(cookieUser);
      }
    } catch {
      setUser(cookieUser);
    }
  }, []);

  useEffect(() => {
    async function loadUser() {
      const cookieUser = parseUserInfo();

      // Dev mode, no cookie. A local auth fixture (APEX_LOCAL_AUTH_FIXTURE on
      // the server) makes /auth/me return a real seeded user even without a
      // cookie. Probe for it: if present, go through /auth/login to establish
      // the SPA cookie and reload as that user. Otherwise fall back to a
      // throwaway dev identity for pure-frontend work with no backend.
      if (import.meta.env.DEV && !cookieUser) {
        try {
          const probe = await apiFetch('/auth/me');
          if (probe.ok) {
            const data = await probe.json();
            if (data.user || data.rep) {
              window.location.href =
                '/auth/login?return_to=' +
                encodeURIComponent(window.location.pathname + window.location.search);
              return;
            }
          }
        } catch {
          // No backend / no fixture — fall through to the dev identity.
        }
        setUser({
          email: 'dev@apexpro.ai',
          name: 'Dev User',
          role: 'admin',
          platformOps: true,
        });
        setIsLoading(false);
        return;
      }

      if (cookieUser?.email) {
        // loadPermissions fills the module-level cache in lib/permissions —
        // awaited here so the sidebar can call hasPermission() synchronously
        // as soon as isLoading flips false.
        await Promise.all([loadUserFromServer(), checkTenantStatus(), loadPermissions()]);
      } else {
        // No cookie — send to the sign-in picker. It 302s straight through to
        // Keycloak for anyone who already has a remembered login door.
        redirectToLogin();
        return;
      }

      setIsLoading(false);
    }

    loadUser();
  }, [loadUserFromServer, checkTenantStatus]);

  const refreshUser = useCallback(async (): Promise<void> => {
    // Rotate the JWT first so the next /auth/me sees the new claims.
    const ok = await refreshToken();
    if (!ok) return;
    // Role/permission changes are exactly what triggers this path — refresh
    // the permission cache alongside the user record.
    await Promise.all([loadUserFromServer(), checkTenantStatus(), loadPermissions()]);
  }, [loadUserFromServer, checkTenantStatus]);

  const login = useCallback((returnTo?: string) => {
    redirectToLogin(returnTo);
  }, []);

  const logout = useCallback(async (opts?: { forgetLoginMethod?: boolean }) => {
    const forget = opts?.forgetLoginMethod === true;
    try {
      const res = await fetch(`/auth/logout${forget ? '?forget=1' : ''}`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.keycloak_logout_url) {
          window.location.href = data.keycloak_logout_url;
          return;
        }
      }
    } catch {
      /* fall through */
    }
    // Forgetting the door means the picker must actually show, even though
    // the cookie is already gone — `?choose=1` guards against a race where a
    // cached Set-Cookie hasn't landed yet.
    window.location.href = forget ? '/login?choose=1' : '/login';
  }, []);

  const switchOrg = useCallback(async (orgId: string) => {
    const res = await fetch('/auth/switch-org', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organizationId: orgId }),
    });
    if (res.ok) window.location.reload();
  }, []);

  /**
   * Derive impersonation state. Platform-ops users carry their real role as
   * 'platform-ops'; their effective role is the impersonated user's
   * membership role when a target is active. Regular users can't
   * impersonate — the server never emits an impersonation block for them.
   */
  const isPlatformOps = user?.platformOps === true;
  const realRole = isPlatformOps ? 'platform-ops' : (user?.role ?? 'rep');
  const effectiveRole = isPlatformOps && impersonation ? impersonation.target.role : realRole;
  const isImpersonating = isPlatformOps && impersonation != null;
  const impersonationTarget = isImpersonating ? (impersonation?.target ?? null) : null;
  const portalView =
    user?.portalOnly === true || (isImpersonating && effectiveRole === 'agency_owner');

  // Point the shared from-number picker at the portal number surface for
  // portal sessions (#1426). Module-level switch: the picker's context
  // cache drops on change so numbers never carry across shells.
  useEffect(() => {
    configurePickerContextUrl(
      portalView ? '/portal/voice/picker-context' : '/api/voice/picker-context',
    );
  }, [portalView]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        tenantError,
        realRole,
        effectiveRole,
        isImpersonating,
        impersonationTarget,
        portalView,
        login,
        logout,
        switchOrg,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
